import { memo, useState, useMemo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { type TChatbotNodeData } from '../node.type'
import { CurecallBadge, CurecallTag, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { useChatbotFlow } from '../chatbot.provider'
import { twMerge } from 'src/utils/tw-merge.util'
import { ChatbotNodeResponses } from './chatbot-node-responses.component'
import { useProjectVariables } from '../chatbot-form/use-project-variables.hook'
import { type TVariable, EVariableType } from 'src/types/variable'
import { START_NODE_ID } from '../chatbot.constants'

type TChatbotNodeProps = {
  id: string
  data: TChatbotNodeData
  selected?: boolean
}

export const ChatbotNode = memo(function ChatbotNode({ id, data: currentNode, selected }: TChatbotNodeProps) {
  const { t } = useTranslation()
  const { edges } = useChatbotFlow()
  const { data: variables = [] } = useProjectVariables()
  const languages = Object.keys(currentNode.messagesByLanguage) as string[]
  const [isHovered, setIsHovered] = useState(false)

  const selectedVariable = useMemo(
    () => variables.find((v: TVariable) => v.id === currentNode.variable?.id),
    [variables, currentNode.variable?.id],
  )

  const isFirstNode = id === START_NODE_ID
  const hasOutgoingEdge = edges.some((edge) => edge.source === id)
  const isNotConnected = !edges.some((edge) => edge.source === id || edge.target === id)
  const isTargetOfSelectedEdge = edges.some((edge) => edge.selected && edge.target === id)
  const isSourceOfSelectedEdge = edges.some((edge) => edge.selected && edge.source === id)

  const handleSizeStyle = { width: '20px', height: '20px' }
  const handleClassName = isTargetOfSelectedEdge
    ? '!c-bg-primary-200 !c-border-2 !c-border-primary-500'
    : '!c-bg-white !c-border !c-border-black'

  const nodeTitle = isFirstNode
    ? t('pages.configuration.followups.chatbot.node-editor.start-node-title')
    : currentNode.title || t('pages.configuration.followups.chatbot.node-editor.new-node')

  return (
    <div
      className={twMerge(
        'c-min-w-[280px] c-bg-white c-rounded-lg c-shadow-md c-border',
        currentNode?.variable?.type === EVariableType.INTEGER_VALUE_RANGE ||
          currentNode?.variable?.type === EVariableType.MEDIA
          ? 'c-max-w-auto'
          : 'c-max-w-[360px]',
        selected ? 'c-border-2 c-border-primary' : 'c-border-grey-500',
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!isFirstNode && (
        <Handle type="target" position={Position.Top} style={handleSizeStyle} className={handleClassName} />
      )}

      {isNotConnected && (
        <div className="c-flex c-justify-center c-pt-2">
          <CurecallBadge color={ECurecallBadgeColor.GREY}>
            {t('pages.configuration.followups.chatbot.node-editor.not-connected')}
          </CurecallBadge>
        </div>
      )}

      <h4 className="c-text-sm c-font-bold c-text-center c-py-2">{nodeTitle}</h4>

      {!isFirstNode && (
        <div className="c-p-2 c-border-t c-border-grey-200">
          {Object.values(currentNode.messagesByLanguage).some((msg) => msg) && (
            <p className="c-p-3 c-mb-1 c-bg-gray-50 c-rounded c-border c-border-gray-200 c-text-xs c-text-gray-700 c-line-clamp-3">
              {Object.values(currentNode.messagesByLanguage).find((msg) => msg) || ''}
            </p>
          )}

          {languages.map((lang) => (
            <CurecallTag key={lang} className="tw-inline-block tw-mr-2 tw-mb-2">
              {lang.toUpperCase()}
            </CurecallTag>
          ))}

          {selectedVariable && (
            <CurecallTag className="tw-inline-block tw-mr-2 tw-mb-2 tw-max-w-full">
              {selectedVariable.intlLabel?.fr || selectedVariable.intlLabel?.en}
            </CurecallTag>
          )}

          {currentNode.humanReviewCheck && currentNode.variable?.type === EVariableType.MEDIA && (
            <CurecallTag status="INFORMATION" className="tw-inline-block tw-mr-2 tw-mb-2">
              {t('pages.configuration.followups.chatbot.conditional-sections.display-in-dashboard')}
            </CurecallTag>
          )}
        </div>
      )}

      <ChatbotNodeResponses
        id={id}
        selected={selected}
        isHovered={isHovered}
        isFirstNode={isFirstNode}
        hasOutgoingEdge={hasOutgoingEdge}
        isSourceOfSelectedEdge={isSourceOfSelectedEdge}
        variable={currentNode.variable}
      />
    </div>
  )
})
